export interface GoogleSheetsConfig {
  spreadsheetId: string;
  ranges: {
    teams: string;
    design: string;
    employees: string;
    backlog: string;
  };
}

export class BackendApiService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    // Use provided baseUrl, environment variable, or default to localhost for development
    this.baseUrl = baseUrl || process.env.REACT_APP_API_URL || '';
  }

  private async makeRequest(endpoint: string): Promise<any> {
    if (!this.baseUrl) {
      throw new Error('Backend API is not configured. Please set REACT_APP_API_URL environment variable.');
    }
    
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`);
      
      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error calling backend API ${endpoint}:`, error);
      throw error;
    }
  }

  async fetchKPIMetrics(): Promise<any> {
    return this.makeRequest('/api/kpi');
  }

  async fetchTeams(): Promise<{ teams: string[], design: string[] }> {
    return this.makeRequest('/api/teams');
  }

  async fetchEmployees(): Promise<any[]> {
    return this.makeRequest('/api/employees');
  }

  async fetchEmployeesTable(team?: string, horizontalTeam?: string, month?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (team) params.append('team', team);
    if (horizontalTeam) params.append('horizontalTeam', horizontalTeam);
    if (month) params.append('month', month);
    
    const endpoint = params.toString() ? `/api/employees-table?${params.toString()}` : '/api/employees-table';
    return this.makeRequest(endpoint);
  }

  async fetchBacklogData(): Promise<any[]> {
    return this.makeRequest('/api/backlog');
  }

  async fetchAllData(): Promise<{
    kpiMetrics: any;
    teams: any[];
    design: string[];
    employees: any[];
    backlogData: any[];
  }> {
    return this.makeRequest('/api/all');
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await this.makeRequest('/api/health');
      return response.status === 'healthy' || response.status === 'OK';
    } catch (error) {
      return false;
    }
  }
}
